import classnames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  Nav,
  NavItem,
} from "reactstrap";
import Swal from "sweetalert2";
import Loading from "../../Components/Loading";
import EmployerGroupsTable from "../../Components/Tables/EmployerGroupTable/EmployerGroupTable";
import AddRoleModal from "../../Components/modals/GeneralModals/AddRoleModal";
import { getRolesForTables } from "../../slices/admin/thunk";
import { useCurrentUser } from "../../slices/auth/hooks";
import { getEmployersByGA, postEmployerByGA } from "../../slices/ga/thunk";
import { DASHBOARD_USER_ROLES } from "../../constants";
import SearchBar from "../../Components/SearchBar/SearchBar";
import ButtonAddRole from "../../Components/Buttons/ButtonAddRole";
import HeadersRole from "../../Components/Headers/HeadersRole";
import employerGroupIcon from "../../assets/images/iconsRoles/employerGroupIcon.svg";

const DashboardGaEmployerGroups = () => {
  const inputRef = useRef(null);
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("1");
  const [ownEmployers, setOwnEmployers] = useState(false);

  const [employers, setEmployers] = useState([]);
  const [totalEmployers, setTotalEmployers] = useState(0);
  const [pageSize, setPageSize] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [addEmployerModal, setAddRoleModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const [searchDone, setSearchDone] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState(searchTerm);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const toggleTab = (tab, status) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setOwnEmployers(status === "own");
      setCurrentPage(0);
    }
  };

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timerId);
  }, [searchTerm]);

  const fetchEmployersGA = async () => {
    try {
      setLoading(true);
      const response = await dispatch(
        getRolesForTables({
          id: currentUser.id,
          userRole: DASHBOARD_USER_ROLES.GA,
          role: "employers",
          status: "active",
          page: currentPage,
          query: searchTerm,
        })
      ).unwrap();
      setEmployers(response.data);
      setTotalEmployers(response.total);
      setPageSize(response.pageSize);
      setTotalPages(response.pages);
      setSearchDone(true);
      setLoading(false);
    } catch (error) {
      setLoading(true);
      console.log(error);
      setLoading(false);
    }
  };

  const getOwnEmployersGA = async () => {
    try {
      setLoading(true);
      const response = await dispatch(
        getEmployersByGA({
          id: currentUser.id,
          page: currentPage,
          status: "active",
          query: debouncedSearchTerm,
        })
      ).unwrap();
      setEmployers(response.data);
      setTotalEmployers(response.total);
      setPageSize(response.pageSize);
      setTotalPages(response.pages);
      setSearchDone(true);
      setLoading(false);
    } catch (error) {
      setLoading(true);
      console.log(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debouncedSearchTerm === lastSearchTerm) {
      setSearchDone(true);
    } else {
      setSearchDone(false);
    }
    if (activeTab == "1") {
      fetchEmployersGA();
    } else {
      getOwnEmployersGA();
    }
  }, [currentPage, dispatch, debouncedSearchTerm, currentUser.id, activeTab]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleToggleModal = () => {
    setAddRoleModal(!addEmployerModal);
  };

  const handleAddEmployer = async (data) => {
    try {
      setLoading(true);
      const response = await dispatch(
        postEmployerByGA({
          employer: data,
          id: currentUser.id,
        })
      ).unwrap();
      Swal.fire({
        icon: "success",
        title: "Employer Group Added",
        text: "Employer Group has been added successfully",
      });
      setEmployers((prevEmployers) => [...prevEmployers, response]);
      setTotalEmployers((prevTotal) => prevTotal + 1);

      setLoading(false);
      setActiveTab("2");
      setAddRoleModal(false);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `${error.message}`,
      });

      setLoading(false);
      console.log(error);
    }
  };
  return (
    <React.Fragment>
      <AddRoleModal
        roleToAdd="Employer Group"
        open={addEmployerModal}
        setOpen={handleToggleModal}
        onCreate={handleAddEmployer}
        loading={loading}
      />
      <div className="page-content px-0 ">
        <Container fluid>
          <HeadersRole
            role="Employer Groups"
            icon={employerGroupIcon}
            svg={true}
            text={""}
          />

          <Card className="mb-4 pb-3 ">
            <CardBody>
              <div className="col-12 d-flex justify-content-between mt-1 align-items-center ">
                <div className="col-4 d-flex justify-content-end">
                  <SearchBar
                    inputRef={inputRef}
                    value={searchTerm}
                    onChange={handleSearch}
                    placeholder="Filter by name or email"
                  />
                </div>

                <ButtonAddRole
                  open={handleToggleModal}
                  roleToAdd="Employer Group"
                />
              </div>

              <Col xl={12}>
                <Nav
                  className="nav-tabs-custom p-2 mb-2 card-header-tabs border-bottom-0 mt-0"
                  role="tablist"
                >
                  <NavItem className="me-3">
                    <NavLink
                      className={classnames(
                        {
                          "active  p-2 text-primary": activeTab === "1",
                          "border-bottom border-2 border-primary":
                            activeTab === "1",
                          "fw-semibold": activeTab === "1",
                          " p-2 text-primary": activeTab !== "1",
                        },
                        { disabled: loading }
                      )}
                      onClick={() => {
                        if (!loading) {
                          toggleTab("1", "all");
                        }
                      }}
                      href="#"
                    >
                      ALL
                    </NavLink>
                    <NavLink
                      className={classnames(
                        {
                          "active  p-2 text-primary": activeTab === "2",
                          "border-bottom border-2 border-primary":
                            activeTab === "2",
                          "fw-semibold": activeTab === "2",
                          " p-2 text-primary": activeTab !== "2",
                        },
                        { disabled: loading }
                      )}
                      onClick={() => {
                        if (!loading) {
                          toggleTab("2", "own");
                        }
                      }}
                      href="#"
                    >
                      MY EMPLOYER GROUPS
                    </NavLink>
                  </NavItem>
                </Nav>

                <div className="table-container  position-relative">
                  {loading && <Loading />}
                  <div className="live-preview">
                    <div className="table-responsive ">
                      <EmployerGroupsTable
                        data={employers}
                        searchDone={searchDone}
                        searchParam={debouncedSearchTerm}
                        paginationProps={{
                          currentPage,
                          totalPages,
                          totalItems: totalEmployers,
                          pageSize,
                          onChangePage: handleChangePage,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Col>
            </CardBody>
          </Card>
        </Container>
      </div>
      <div id="portal-root"></div>
    </React.Fragment>
  );
};

export default DashboardGaEmployerGroups;
